import { EmailSchema, formatMoney } from "@iec-id/shared";
import type { ReferralInviter } from "@iec-id/shared/contracts/referrals";
import { AvatarIcon, Button, Hint, Input, initialsOf, LinkButton, Spinner } from "@iec-id/ui";
import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInviteT } from "@/components/invite/invite-i18n";
import { ServiceTiles } from "@/components/invite/service-tiles";
import { IecMark } from "@/components/shared/iec-mark";
import { RewardCallout } from "@/components/shared/reward-callout";
import { AuthPending, OrDivider } from "@/components/signin/auth-card";
import { GoogleMark } from "@/components/signin/brand-mark";
import { LocaleSwitcher } from "@/components/signin/locale-switcher";
import { useSigninT } from "@/components/signin/signin-i18n";
import { SupportLink } from "@/components/support/support-link";
import {
	type AuthSearch,
	fallbackDestination,
	maskEmail,
	needsAccountChooser,
	parseAuthSearch,
	resolveService,
	safeExternalUrl,
	savePendingSignIn,
	wantsExplicitForm,
} from "@/lib/auth-flow";
import { parseEmailInput } from "@/lib/email-input";
import { useLocale } from "@/lib/locale";
import { api } from "@/lib/trpc";

export const Route = createFileRoute("/ref/$code")({
	validateSearch: parseAuthSearch,
	component: InviteLanding,
});

/** Единственная проверка адреса — контрактная схема, второй копии правил нет. */
const isContractEmail = (value: string): boolean => EmailSchema.safeParse(value).success;

/** Генератор выдаёт A-Z0-9, 6–12 знаков (`ReferralCodeSchema`). */
const CODE_PATTERN = /^[A-Z0-9]{6,12}$/;

/**
 * Лендинг приглашения `/ref/CODE` (макет 4c).
 *
 * Публичная страница для нового человека: о пригласившем показываем ровно то,
 * что отдаёт `resolveCode`, — имя и аватар. Ни почты, ни маски почты, ни `sub`.
 *
 * Если сессия уже жива — бонус задним числом не ставим (связь только при
 * регистрации). Уводим в сервис из `?s=` / `service`, иначе на витрину.
 *
 * Неизвестный код — не 404. Человек пришёл по ссылке от друга и должен
 * получить возможность зарегистрироваться; исчезает только обещание бонуса.
 */
function InviteLanding() {
	const { code: rawCode } = Route.useParams();
	const search = Route.useSearch();
	const navigate = useNavigate();
	const t = useInviteT();
	const tSignin = useSigninT();
	const { t: tCommon } = useTranslation("common");

	const explicitForm = wantsExplicitForm(search);
	const service = resolveService(search);
	const hint = api.auth.sessionHint.useQuery(service ? { serviceId: service.id } : {}, {
		enabled: !explicitForm,
		retry: false,
		staleTime: 0,
		refetchOnWindowFocus: false,
	});
	const me = api.auth.me.useQuery(undefined, {
		enabled: !explicitForm && hint.data?.state === "session",
		retry: false,
	});
	const hasSession = hint.data?.state === "session";

	const code = rawCode.trim().toUpperCase();
	const wellFormed = CODE_PATTERN.test(code);
	const resolved = api.referrals.resolveCode.useQuery(
		{ code },
		// Кривой код на сервер не отправляем: ответ известен заранее, а лишний
		// публичный запрос — это подарок тому, кто перебирает коды.
		{ enabled: wellFormed && (explicitForm || !hasSession), retry: false, staleTime: 5 * 60_000 },
	);

	useEffect(() => {
		if (explicitForm || !hasSession || !me.data) return;
		if (me.data.needsOnboarding) {
			void navigate({ to: "/onboarding", search, replace: true });
			return;
		}
		if (needsAccountChooser(search)) {
			void navigate({ to: "/continue", search, replace: true });
			return;
		}
		window.location.assign(fallbackDestination(search));
	}, [explicitForm, hasSession, me.data, navigate, search]);

	if (!explicitForm && (hint.isPending || hasSession)) {
		return <AuthPending label={tSignin("email.checkingSession")} />;
	}

	const state = !wellFormed || resolved.isError ? { state: "unknown" as const } : resolved.data;

	return (
		<div className="flex w-full max-w-[1000px] flex-col gap-6 py-6">
			<InviteHeader search={search} />

			<div className="grid gap-4 lg:grid-cols-[1fr_320px]">
				<section className="flex flex-col gap-6 rounded-card bg-card p-6 sm:p-8">
					{state === undefined ? (
						<div
							role="status"
							aria-live="polite"
							className="flex min-h-60 flex-col items-center justify-center gap-3 text-fg-muted"
						>
							<Spinner size={24} />
							<span className="t-meta">{t("state.loadingAria")}</span>
						</div>
					) : (
						<>
							{state.state === "unknown" ? null : (
								<Inviter inviter={state.inviter} serviceName={service?.name} />
							)}

							<h1 className="t-page-title text-fg">
								{state.state === "unknown" ? (
									t("noCode.headline")
								) : (
									<>
										{/* display:none убирает вариант и из дерева доступности,
										    поэтому скринридер читает ровно один заголовок. */}
										<span className="hidden sm:inline">
											{service ? t("headlineService", { service: service.name }) : t("headline")}
										</span>
										<span className="sm:hidden">
											{service
												? t("headlineServiceMobile", { service: service.name })
												: t("headlineMobile")}
										</span>
									</>
								)}
							</h1>

							{state.state === "unknown" ? (
								<p className="t-body text-fg-secondary">{t("noCode.description")}</p>
							) : null}

							{state.state === "valid" ? (
								<RewardCallout
									tone="lead"
									title={t("reward.points", { count: state.welcomeRewardPoints })}
									caption={
										<span className="inline-flex items-center gap-2">
											<RewardWorth minor={state.welcomeRewardMinor} currency={state.currency} />
											<Hint label={t("reward.tooltip")} triggerLabel={tCommon("action.hint")} />
										</span>
									}
								/>
							) : null}

							{state.state === "exhausted" ? (
								<p className="t-body text-fg-secondary">{t("exhausted")}</p>
							) : null}

							<JoinActions search={search} code={wellFormed ? code : undefined} />
						</>
					)}
				</section>

				<ServiceTiles highlightId={service?.id} />
			</div>

			{/*
			 * «Поддержка» — та же ссылка и тот же попап, что в подвале карточки
			 * входа. Здесь она нужна не меньше: человек по эту сторону ссылки
			 * аккаунта ещё не имеет, и если код приглашения не принимается или
			 * письмо не приходит, написать ему больше некуда — почтового адреса
			 * на лендинге нет вовсе.
			 *
			 * Сервис из `?s=` уходит вместе с обращением: дежурному важно, из
			 * какой ссылки человек пришёл.
			 */}
			<div className="flex justify-center">
				<SupportLink context={{ serviceId: service?.id ?? null }} />
			</div>
		</div>
	);
}

/**
 * Шапка лендинга: знак, слово, выбор языка и вход для тех, у кого аккаунт уже
 * есть.
 *
 * Язык здесь обязателен (решение владельца 22.08.2026): ссылку присылают в
 * личном сообщении, и открыть её может человек, чей браузер говорит на другом
 * языке, — а это первый экран экосистемы, и уйти с него «непонятно» нельзя.
 */
function InviteHeader({ search }: { search: AuthSearch }) {
	const t = useInviteT();
	const navigate = useNavigate();

	return (
		<header className="flex items-center justify-between gap-4">
			<div className="flex items-center gap-3">
				<IecMark size={32} alt={t("header.markAlt")} className="sm:hidden" />
				<IecMark size={48} alt={t("header.markAlt")} className="hidden sm:block" />
				<span className="t-wordmark text-fg">{t("header.brand")}</span>
			</div>
			<div className="flex items-center gap-4">
				<LocaleSwitcher />
				<LinkButton onClick={() => void navigate({ to: "/", search: withInviteContext(search) })}>
					{t("header.signIn")}
				</LinkButton>
			</div>
		</header>
	);
}

/** «Maya приглашает вас в StudyHub» — аватар и имя, больше о пригласившем ничего. */
function Inviter({
	inviter,
	serviceName,
}: {
	inviter: ReferralInviter;
	serviceName: string | undefined;
}) {
	const t = useInviteT();
	return (
		<div className="flex items-center gap-3">
			<AvatarIcon
				size={48}
				initials={initialsOf(inviter.displayName)}
				{...(inviter.avatarUrl ? { src: inviter.avatarUrl } : {})}
				alt={inviter.displayName}
			/>
			<span className="t-row text-fg-secondary">
				{serviceName
					? t("inviterService", { name: inviter.displayName, service: serviceName })
					: t("inviter", { name: inviter.displayName })}
			</span>
		</div>
	);
}

/** «это $7 скидки на первую программу» — цена бонуса, а не абстрактные Баллы. */
function RewardWorth({ minor, currency }: { minor: number; currency: string }) {
	const t = useInviteT();
	const locale = useLocale();
	return <>{t("reward.worth", { amount: formatMoney(minor, currency, locale) })}</>;
}

/**
 * Две кнопки присоединения. Код приглашения уезжает с ними обеими: у Google —
 * параметром `referralCode` (сессия появится уже на нашей стороне), у почты —
 * в адресной строке экрана входа, где его подхватит `requestEmailCode`.
 */
function JoinActions({ search, code }: { search: AuthSearch; code: string | undefined }) {
	const t = useInviteT();
	const tSignin = useSigninT();
	const locale = useLocale();
	const navigate = useNavigate();
	const [failure, setFailure] = useState<string | null>(null);

	const googleStart = api.auth.googleStart.useMutation({
		onSuccess: (data) => window.location.assign(data.authorizationUrl),
		onError: () => setFailure(t("join.googleFailed")),
	});

	const dest = withInviteContext(search);
	const nextSearch: AuthSearch = code ? { ...dest, ref: code } : dest;

	// Тот же путь, что у экрана входа: снимок `pending` + переход на `/code`.
	// Своей копии логики здесь нет — расхождение означало бы, что письмо ушло,
	// а экран кода про него не знает.
	const requestCode = api.auth.requestEmailCode.useMutation({
		onSuccess: (data, variables) => {
			setFailure(null);
			const now = Date.now();
			savePendingSignIn({
				email: variables.email,
				maskedEmail: data.maskedEmail,
				expiresAt: now + data.expiresInSec * 1000,
				resendAt: now + data.resendAfterSec * 1000,
			});
			void navigate({ to: "/code", search: nextSearch });
		},
		onError: (error, variables) => {
			// 429 — письмо уже улетело: человеку нужен экран кода, а не пустое поле.
			if (error.data?.httpStatus === 429) {
				savePendingSignIn({
					email: variables.email,
					maskedEmail: maskEmail(variables.email),
					expiresAt: null,
					resendAt: Date.now() + 60_000,
				});
				void navigate({ to: "/code", search: nextSearch });
				return;
			}
			setFailure(tSignin("email.errors.failed"));
		},
	});

	const form = useForm({
		defaultValues: { email: "" },
		onSubmit: async ({ value }) => {
			setFailure(null);
			// Адрес уезжает в ASCII: контракт юникода не принимает (В5).
			const parsed = parseEmailInput(value.email, isContractEmail);
			if (parsed.state !== "ok") return;
			await requestCode.mutateAsync({
				email: parsed.value,
				purpose: "sign_in",
				locale,
				...(dest.service ? { serviceId: dest.service } : {}),
				// Реф-код обязан уехать и здесь, иначе связь с пригласившим
				// теряется, а с ней приветственные Баллы обоим (К1).
				...(code ? { referralCode: code } : {}),
			});
		},
	});

	const busy = requestCode.isPending || googleStart.isPending;

	return (
		<div className="flex flex-col gap-2.5">
			{failure ? (
				<p role="alert" className="t-body text-danger">
					{failure}
				</p>
			) : null}

			{/* Вторичная, как на форме входа: зелёная заливка тут обещала «главное
			    действие», хотя способов входа два и оба равнозначны. */}
			<Button
				variant="second"
				fullWidth
				iconLeft={<GoogleMark />}
				state={googleStart.isPending ? "loading" : "default"}
				onClick={() => {
					setFailure(null);
					const redirectTo = safeExternalUrl(dest.redirect_to);
					googleStart.mutate({
						...(code ? { referralCode: code } : {}),
						...(redirectTo ? { redirectTo } : {}),
						...(dest.service ? { serviceId: dest.service } : {}),
					});
				}}
			>
				{t("join.google")}
			</Button>

			<OrDivider label={tSignin("email.or")} />

			{/* Почта вводится ЗДЕСЬ. Раньше кнопка уводила на общий экран входа —
			    человек, только что читавший «вас пригласили», оказывался на
			    «Войти в аккаунт», и приглашение будто исчезало. Код всё так же
			    уходит на `/code`, вместе с реф-кодом. */}
			<form
				noValidate
				className="flex flex-col gap-2.5"
				onSubmit={(event) => {
					event.preventDefault();
					void form.handleSubmit();
				}}
			>
				<form.Field
					name="email"
					validators={{
						onSubmit: ({ value }) => {
							const parsed = parseEmailInput(value, isContractEmail);
							return parsed.state === "ok" ? undefined : "invalid";
						},
					}}
				>
					{(field) => (
						<Input
							label={tSignin("email.field")}
							type="email"
							name="email"
							autoComplete="email"
							inputMode="email"
							enterKeyHint="go"
							value={field.state.value}
							disabled={busy}
							onChange={(event) => field.handleChange(event.target.value)}
							onBlur={field.handleBlur}
							{...(field.state.meta.errors.length > 0
								? { errorText: tSignin("email.errors.invalid") }
								: {})}
						/>
					)}
				</form.Field>

				<Button
					type="submit"
					variant="primary"
					fullWidth
					state={requestCode.isPending ? "loading" : "default"}
				>
					{t("join.email")}
				</Button>
			</form>
		</div>
	);
}

/** Реф-ссылка с `?s=` должна увести в этот сервис после входа, а не на витрину. */
function withInviteContext(search: AuthSearch): AuthSearch {
	const service = resolveService(search);
	if (!service) return search;
	return {
		...search,
		service: service.id,
		redirect_to: search.redirect_to ?? service.url,
	};
}
